import { ENTITY_TYPES } from '@sales-center/common'

import { leadFieldConfig } from './leadFieldConfig'
import { opportunityFieldConfig } from './opportunityFieldConfig'
import { RequiredGroupName } from './requiredGroups'
import { FieldConfig, FieldRenderer } from './typings'

/**
 * Lead and Opportunity field ids do overlap (e.g. `salesTeamId`,
 * `campaignId`), so the registry is keyed by entity type as well as field id
 * — a field-id-only key would let one entity's entry silently overwrite the
 * other's.
 */
const registryKey = (entityType: ENTITY_TYPES, fieldId: string): string =>
  `${entityType}:${fieldId}`

/**
 * Field+entity to registry entry, for both entity types.
 *
 * Built once at module scope so any consumer can resolve a field's display
 * order, renderer, combine state, and required group for the entity it is
 * rendering.
 *
 * This is the single read path into the registry: nothing outside `config/`
 * should reach for the source arrays in `registryData.ts` directly.
 */
const fieldConfigsById = new Map<string, FieldConfig>([
  ...leadFieldConfig.map((config): [string, FieldConfig] => [
    registryKey(ENTITY_TYPES.Lead, config.id),
    config,
  ]),
  ...opportunityFieldConfig.map((config): [string, FieldConfig] => [
    registryKey(ENTITY_TYPES.Opportunity, config.id),
    config,
  ]),
])

/**
 * A field's position in the settings-page display order.
 *
 * Returns `-1` for a field the registry does not know about, so an unregistered
 * field sorts ahead of every registered one — the same position the raw
 * `customOrder.indexOf` lookup this replaces would have given it.
 */
export const getFieldOrder = (
  entityType: ENTITY_TYPES,
  fieldId: string,
): number => fieldConfigsById.get(registryKey(entityType, fieldId))?.order ?? -1

/**
 * The "at least one of" group a field belongs to, or `undefined` if it is not
 * part of one.
 */
export const getFieldRequiredGroup = (
  entityType: ENTITY_TYPES,
  fieldId: string,
): RequiredGroupName | undefined =>
  fieldConfigsById.get(registryKey(entityType, fieldId))?.requiredGroup

/** Which card body a field renders, defaulting to the shared one. */
export const getFieldRenderer = (
  entityType: ENTITY_TYPES,
  fieldId: string,
): FieldRenderer =>
  fieldConfigsById.get(registryKey(entityType, fieldId))?.renderer ?? 'default'

/** Whether a field's card joins visually to the card below it. */
export const combinesWithNextField = (
  entityType: ENTITY_TYPES,
  fieldId: string,
): boolean =>
  !!fieldConfigsById.get(registryKey(entityType, fieldId))?.combineWithNext
